import { FormEvent, KeyboardEvent, useState, useRef } from 'react';
import { Paperclip, Send, Loader2 } from 'lucide-react';

interface GroupMessageInputProps {
  onSendMessage: (messageText: string) => Promise<void> | void;
  onSelectFile: (file: File) => Promise<void> | void;
  onTyping?: (isTyping: boolean) => void;
  disabled?: boolean;
}

export default function GroupMessageInput({
  onSendMessage,
  onSelectFile,
  onTyping,
  disabled = false
}: GroupMessageInputProps) {
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleTyping = (isTyping: boolean) => {
    if (!onTyping || disabled) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (isTyping !== isTypingRef.current) {
      isTypingRef.current = isTyping;
      onTyping(isTyping);
    }

    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        if (isTypingRef.current) {
          isTypingRef.current = false;
          onTyping(false);
        }
      }, 3000);
    }
  };

  const sendMessage = async () => {
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage || isSending || disabled) {
      return;
    }

    try {
      setIsSending(true);
      
      if (isTypingRef.current) {
        handleTyping(false);
      }
      
      await onSendMessage(trimmedMessage);
      setMessageText('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendMessage();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setMessageText(value);

    if (value.trim()) {
      handleTyping(true);
    } else {
      handleTyping(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || isUploading || disabled) return;

    try {
      setIsUploading(true);
      await onSelectFile(file);
    } catch (error) {
      console.error('Failed to attach file:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="sticky bottom-0 border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
      <div className="mx-auto flex max-w-4xl items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileChange}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isSending || isUploading || disabled}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Attach file"
        >
          {isUploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
        </button>

        <input
          value={messageText}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? "Waiting for connection..." : "Type a message..."}
          disabled={isSending || disabled}
          className="h-11 min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-4 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Type a message"
        />

        <button
          type="submit"
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          disabled={!messageText.trim() || isSending || disabled}
          aria-label="Send message"
        >
          {isSending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </button>
      </div>
    </form>
  );
}